import React from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";

import { IMG, SERVICES } from "../lib/assets";
import { Reveal, AnimatedHeading, FadeImage } from "../components/Reveal";
import { PageShell, PageHeader } from "../components/PageShell";
import { PaisleyDivider } from "../components/Motifs";

function ServiceBlock({ service, index }) {
  const reversed = index % 2 === 1;
  return (
    <section
      id={service.id}
      className="mx-auto max-w-wide scroll-mt-28 px-6 py-20 sm:px-12 sm:py-28"
    >
      <div
        className={`grid grid-cols-1 items-center gap-12 lg:gap-24 ${
          reversed
            ? "md:grid-cols-[1.618fr_1fr]"
            : "md:grid-cols-[1fr_1.618fr]"
        }`}
      >
        <div className={reversed ? "md:order-2" : ""}>
          <Reveal>
            <span className="font-display text-7xl text-gold/30">{service.no}</span>
          </Reveal>
          <AnimatedHeading
            text={service.name}
            className="mt-2 font-display text-5xl font-light text-cream-50 sm:text-6xl"
          />
          <Reveal delay={0.15}>
            <p className="mt-2 italic font-accent text-lg text-gold">
              {service.tagline}
            </p>
            <p className="mt-6 max-w-md text-cream-50/70 leading-relaxed">
              {service.desc}
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <ul className="mt-8 flex flex-col divide-y divide-cream-50/10 border-t border-cream-50/10">
              {service.treatments.map(([name, price]) => (
                <li
                  key={name}
                  className="flex items-center justify-between py-4"
                >
                  <span className="flex items-center gap-3 text-cream-50/85">
                    <Check size={16} className="text-gold" />
                    {name}
                  </span>
                  <span className="font-display text-xl text-gold">{price}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.3}>
            <Link
              to="/contact"
              data-testid={`service-book-${service.id}`}
              className="mt-8 inline-block rounded-full bg-gold px-8 py-4 text-xs uppercase tracking-[0.2em] text-ink-900 transition-colors hover:bg-antique"
            >
              Book this service
            </Link>
          </Reveal>
        </div>

        <div className={`relative ${reversed ? "md:order-1" : ""}`}>
          <FadeImage
            src={service.image}
            alt={service.name}
            className="aspect-[4/5] w-full"
          />
          <div className="absolute inset-0 rounded-2xl border border-gold/20" />
        </div>
      </div>
    </section>
  );
}

export default function Services() {
  return (
    <PageShell>
      <PageHeader
        overline="Our Craft"
        title="Services for every facet of you."
        subtitle="Hair, skin, bridal and nails — each ritual designed with intention, heritage and uncompromising detail."
        image={IMG.interiorVanity}
      />

      <div className="border-b border-gold/15 bg-ink-800/40">
        <div className="mx-auto flex max-w-wide flex-wrap gap-x-8 gap-y-3 px-6 py-6 sm:px-12">
          {SERVICES.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              data-testid={`service-anchor-${s.id}`}
              className="link-underline text-sm uppercase tracking-[0.18em] text-cream-50/70 hover:text-gold"
            >
              {s.name}
            </a>
          ))}
        </div>
      </div>

      {SERVICES.map((s, i) => (
        <React.Fragment key={s.id}>
          <ServiceBlock service={s} index={i} />
          {i < SERVICES.length - 1 && <PaisleyDivider />}
        </React.Fragment>
      ))}

      <section className="py-24 text-center">
        <AnimatedHeading
          text="Not sure where to begin?"
          className="font-display text-4xl font-light text-cream-50 sm:text-5xl"
        />
        <Reveal delay={0.2}>
          <Link
            to="/contact"
            className="mt-8 inline-block rounded-full border border-gold/40 px-8 py-4 text-xs uppercase tracking-[0.2em] text-gold transition-colors hover:bg-gold/10"
          >
            Speak with an artist
          </Link>
        </Reveal>
      </section>
    </PageShell>
  );
}
